import { MouseEvent, useState } from "react";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import { selectViewedGroups } from "@store/selectors";
import ContextMenu from "@shared/components/ContextMenu";
import TextAreaModal from "@shared/components/TextAreaModal";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  deleteGroupAndMembers,
  removeGroup,
  setGroupMeta,
} from "@store/slices/workspaceSlice";
import { closeGroupContextMenu } from "@features/annogram/canvas/state/canvasAuxSlice";

// Curated swatch palette. Picked so every fill passes WCAG AA (3:1
// against white) when used as the frame stroke / label border. Names
// match Tailwind's *-700 family.
const COLOR_SWATCHES: { name: string; value: string }[] = [
  { name: "Default", value: "" },
  { name: "Slate", value: "#475569" },
  { name: "Red", value: "#b91c1c" },
  { name: "Amber", value: "#b45309" },
  { name: "Emerald", value: "#15803d" },
  { name: "Sky", value: "#0369a1" },
  { name: "Violet", value: "#6d28d9" },
  { name: "Pink", value: "#be185d" },
];

/**
 * Right-click context menu for a group's frame. Opened by setting
 * canvasAux.groupContextMenuId. Mirrors RectangleContextMenu's pattern
 * (singleton mount in Canvas.tsx, reads anchor coords from the same
 * aux slice as the rect / arrow menus).
 *
 * Actions:
 *   - Rename: opens TextAreaModal pre-filled with the group's name.
 *   - Set color: opens a swatch submenu; clearing reverts to default.
 *   - Ungroup: dissolve the grouping, keep all member rects.
 *   - Delete group + members: drop the group AND every rect it
 *     contains. Re-indexes arrows and other groups' member references
 *     via deleteGroupAndMembers.
 */
const GroupContextMenu = () => {
  const dispatch = useAppDispatch();
  const groups = useAppSelector(selectViewedGroups);
  const { groupContextMenuId, coords } = useAppSelector(
    (state) => state.canvasAux
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [colorAnchor, setColorAnchor] = useState<HTMLLIElement | null>(null);

  if (groupContextMenuId === null) return null;

  const group = groups.find((g) => g.id === groupContextMenuId);
  if (!group) return null;

  const closeMenu = () => {
    dispatch(closeGroupContextMenu());
    setColorAnchor(null);
  };

  const handleUngroup = () => {
    dispatch(removeGroup({ groupId: group.id }));
    closeMenu();
  };

  const handleDeleteAll = () => {
    if (
      !window.confirm(
        `Delete this group and its ${group.memberRectIndices.length} member${
          group.memberRectIndices.length === 1 ? "" : "s"
        }? This can't be undone.`
      )
    ) {
      closeMenu();
      return;
    }
    dispatch(deleteGroupAndMembers({ groupId: group.id }));
    closeMenu();
  };

  const handleColorClick = (event: MouseEvent<HTMLLIElement>) => {
    // Anchor the swatch submenu to the clicked menu item, same
    // pattern as RectangleContextMenu's "Change Category" submenu.
    setColorAnchor(event.currentTarget);
  };

  const handleColorSelect = (value: string) => {
    dispatch(
      setGroupMeta({
        groupId: group.id,
        color: value || undefined,
      })
    );
    closeMenu();
  };

  const handleRenameSubmit = (
    _e: React.MouseEvent<HTMLButtonElement>,
    input: string
  ) => {
    const next = input.trim();
    if (next) {
      dispatch(setGroupMeta({ groupId: group.id, name: next }));
    }
    setModalOpen(false);
    closeMenu();
  };

  return (
    <>
      <ContextMenu
        open={!colorAnchor && !modalOpen}
        menuCoords={coords}
        menuButtons={[
          { text: "Rename", onClick: () => setModalOpen(true) },
          { text: "Set color", onClick: handleColorClick },
          { text: "Ungroup", onClick: handleUngroup },
          { text: "Delete group and members", onClick: handleDeleteAll },
        ]}
        onClose={closeMenu}
      />
      <Menu
        anchorEl={colorAnchor}
        open={Boolean(colorAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <IconButton
          sx={{ position: "absolute", top: 0, right: 0 }}
          onClick={closeMenu}
        >
          <CloseIcon sx={{ fontSize: 13 }} />
        </IconButton>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, p: 1.5, mt: 1, maxWidth: 200 }}>
          {COLOR_SWATCHES.map((swatch) => {
            const selected =
              swatch.value === (group.color ?? "") ||
              (swatch.value === "" && !group.color);
            return (
              <Box
                key={swatch.name}
                role="button"
                aria-label={swatch.name}
                onClick={() => handleColorSelect(swatch.value)}
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "6px",
                  cursor: "pointer",
                  background: swatch.value || "transparent",
                  border: 2,
                  borderColor: selected ? "primary.main" : "divider",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: swatch.value ? "#fff" : "text.primary",
                }}
              >
                {swatch.value === "" && (
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, fontSize: 10 }}
                  >
                    auto
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      </Menu>
      {modalOpen && (
        <TextAreaModal
          title="Rename group"
          initialInput={group.name ?? ""}
          handleSubmit={handleRenameSubmit}
          handleCancel={() => {
            setModalOpen(false);
            closeMenu();
          }}
        />
      )}
    </>
  );
};

export default GroupContextMenu;
